import React from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { ROUTES } from '../../constants/routes'
import { useAuth } from '../../hooks/useAuth'
import { useRouter } from '../../hooks/useRouter'
import { fromQueryParams, toQueryParams } from '../../utils/query-params'
import { DashboardLayout } from '../DashboardLayout/DashboardLayout'
import { AuthErrorView } from '../ErrorBoundary/AuthErrorView'
import { DashboardErrorView } from '../ErrorBoundary/DashboardErrorView'
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary'

interface IProps {
  isProtected?: boolean
  isBounded?: boolean
}

export const RequireAuth = ({
  isProtected = false,
  isBounded = false,
  children,
}: React.PropsWithChildren<IProps>) => {
  const auth = useAuth()
  const { user, isError, error } = auth
  const { pathname, location } = useRouter()

  const isUnauthenticated = !user?.is_email_verified || (isError && error?.code !== 401)
  const isNotInRedirectLoop = pathname !== ROUTES.LOGIN

  if (isProtected && isUnauthenticated && isNotInRedirectLoop) {
    let params: string = ''
    const { search } = location
    const q = fromQueryParams(search)
    if (pathname !== ROUTES.LOGOUT) {
      params = toQueryParams({
        ...q,
        goto: pathname,
      })
    } else if (search) {
      params = toQueryParams(q)
    }
    const newRoute = ROUTES.LOGIN + (params ? `?${params}` : '')
    return <Navigate to={newRoute} replace />
  }

  if (!isProtected || pathname === ROUTES.SETUP) {
    return (
      <ErrorBoundary {...auth} ErrorView={AuthErrorView}>
        <Outlet />
        {children}
      </ErrorBoundary>
    )
  }

  if (isBounded) {
    return (
      <ErrorBoundary {...{ ...auth, isBounded }} ErrorView={DashboardErrorView}>
        <Outlet />
        {children}
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary {...auth} ErrorView={DashboardErrorView}>
      <Outlet />
      {children}
    </ErrorBoundary>
  )
}

export const RequireAuthDashboard = (props: IProps) => (
  <DashboardLayout>
    <RequireAuth {...props} isBounded={true} />
  </DashboardLayout>
)
